import { useEffect, useRef, useState, useMemo, useDeferredValue } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BackToAdminButton from "../components/BackToAdminButton";
import Autoplay from "embla-carousel-autoplay";
import { aspectInfo } from "../utils/bannerAspect";
import {
  Beef,
  Milk,
  Egg,
  Carrot,
  Apple,
  Croissant,
  ArrowRight,
  User,
  Package,
  LogOut,
  Gift,
  Sparkles,
  Trophy,
  Medal,
  Calendar,
  History,
  Search,
  X,
  Mic,
  Square as MicStop,
  UserCog,
  Drumstick,
  ShoppingBasket,
  CupSoda,
  Crown,
  Headphones,
  FileText,
  Snowflake,
  AlertCircle,
  MapPin,
  Candy,
  Cake,
  SprayCan,
  Nut,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import ProductModal from "../components/ProductModal";
import ProductImage from "../components/ProductImage";
import JumlatyLogo from "../components/JumlatyLogo";
import CustomerServiceModal from "../components/CustomerServiceModal";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { notify, customerStatusVoice } from "../utils/voice";
import { smartMatch } from "../utils/arabicSearch";
import { inStockFirst, isInStock } from "../utils/stockSort";
import { confirmDialog } from "../utils/confirmDialog";
import ContestStrip from "../components/ContestStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  beef: Beef,
  milk: Milk,
  egg: Egg,
  carrot: Carrot,
  apple: Apple,
  croissant: Croissant,
  drumstick: Drumstick,
  groceries: ShoppingBasket,
  beverages: CupSoda,
  dairy: Milk,
  snowflake: Snowflake,
  // New categories added Feb 2026: cake = الحلويات (wrapped cake per user
  // request), spray-can = المنظفات, nut = الأجباس والمكسرات. Icon key names
  // match the `icon` field seeded into the categories collection by the
  // backend startup migration. `candy` kept as a fallback for old rows.
  candy: Candy,
  cake: Cake,
  "spray-can": SprayCan,
  nut: Nut,
  placeholder: null,
};

const SectionCarousel = ({ slides, testId, height = "200px" }) => {
  const autoplay = useRef(Autoplay({ delay: 3500, stopOnInteraction: false, stopOnMouseEnter: true }));
  return (
  <Carousel opts={{ align: "start", loop: true }} plugins={[autoplay.current]} className="w-full">
    <CarouselContent>
      {slides.map((slide, index) => (
        <CarouselItem key={index}>
          <motion.div
            data-testid={`${testId}-slide-${index}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-2xl sm:rounded-3xl p-10 text-center text-white relative overflow-hidden"
            style={{ backgroundColor: slide.color, height }}
          >
            <motion.div
              animate={{
                y: [0, -8, 0, 8, 0],
                rotate: [0, 1, 0, -1, 0],
              }}
              transition={{
                duration: 6 + index,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <h3 className="font-heading font-bold text-2xl sm:text-4xl mb-2">
                {slide.title}
              </h3>
              <p className="text-base sm:text-lg opacity-90">{slide.subtitle}</p>
            </motion.div>
          </motion.div>
        </CarouselItem>
      ))}
    </CarouselContent>
    <CarouselPrevious className="left-4" />
    <CarouselNext className="right-4" />
  </Carousel>
  );
};

const ImageCarousel = ({ banners, defaultSlides, testId, height = "220px", aspect = null }) => {
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
  // Hand-rolled carousel — Embla was unreliable when banners were uploaded
  // one-by-one (a fresh second upload sometimes wouldn't get measured).
  // A simple index + setInterval is bulletproof for the 2–3 banners we host.
  // NOTE: hooks must be called unconditionally before any early return.
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    // Clamp the active index when banner count shrinks (e.g. after delete)
    if (activeIdx >= banners.length) setActiveIdx(0);
  }, [banners.length, activeIdx]);
  useEffect(() => {
    if (banners.length < 2) return; // no cycling needed
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % banners.length);
    }, 3000); // user-requested 3-second cadence
    return () => clearInterval(id);
  }, [banners.length]);

  // Use uploaded banner images if available, otherwise default text slides
  if (banners.length === 0) {
    return <SectionCarousel slides={defaultSlides} testId={testId} height={height} />;
  }
  // When an aspect ratio is provided, use it (so the carousel matches the
  // exact ratio the admin cropped to — no letterboxing, no over-crop). Falls
  // back to a fixed height for backwards-compat.
  const itemStyle = aspect ? { aspectRatio: aspect } : { height };

  const safeIdx = Math.min(activeIdx, banners.length - 1);

  return (
    <div className="relative w-full" data-testid={`${testId}-carousel`}>
      <div
        className="rounded-2xl sm:rounded-3xl overflow-hidden relative bg-[#F3F6F4] w-full"
        style={itemStyle}
      >
        {/* Layer every banner on top of each other; fade in only the active one */}
        {banners.map((banner, index) => (
          <motion.div
            key={banner.id}
            data-testid={`${testId}-image-${index}`}
            initial={false}
            animate={{ opacity: index === safeIdx ? 1 : 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="absolute inset-0"
            style={{ pointerEvents: index === safeIdx ? "auto" : "none" }}
          >
            {banner.image_path ? (
              <img
                src={`${apiBase}/files/${banner.image_path}`}
                alt="banner"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div
                className="absolute inset-0 w-full h-full flex items-center justify-center"
                style={{ backgroundColor: banner.color }}
              >
                <span className="text-white font-bold text-2xl">{banner.title || ""}</span>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Dot indicators — visible only with 2+ banners */}
      {banners.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10"
          data-testid={`${testId}-dots`}>
          {banners.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveIdx(i)}
              aria-label={`الانتقال للإعلان ${i + 1}`}
              data-testid={`${testId}-dot-${i}`}
              className={`transition-all duration-300 rounded-full ${
                i === safeIdx
                  ? "w-6 h-2 bg-white"
                  : "w-2 h-2 bg-white/60 hover:bg-white/90"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProductGrid = ({ products, loading, onSelect, variant = "default", testIdPrefix }) => {
  const isOffer = variant === "offer";
  const isPrize = variant === "prize";
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
      {loading
        ? Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <div className="aspect-square bg-[#E0F2FE] rounded-2xl animate-pulse" />
            </div>
          ))
        : products.map((product, index) => {
            const outOfStock = (product.stock ?? 0) <= 0;
            return (
              <motion.div
                key={`${testIdPrefix}-${product.id}`}
                data-testid={`${testIdPrefix}-${product.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.03, 0.24) }}
                whileHover={!outOfStock ? { y: -6 } : {}}
                onClick={() => !outOfStock && onSelect(product)}
                className={`rounded-2xl overflow-hidden transition-all relative ${
                  outOfStock ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:shadow-lg"
                } ${
                  isPrize
                    ? "bg-[#E0F2FE] border-2 border-[#E85D45]"
                    : "bg-[#E0F2FE] border border-[#BAE6FD]"
                }`}
              >
                {outOfStock && (
                  <div className="absolute inset-0 bg-black/40 z-30 flex items-center justify-center">
                    <span className="bg-[#E85D45] text-white font-bold px-4 py-2 rounded-full text-sm">
                      غير متوفر
                    </span>
                  </div>
                )}
                {isPrize && !outOfStock && (
                  <motion.div
                    className="absolute top-2 right-2 bg-[#E85D45] text-white text-xs px-3 py-1 rounded-full font-bold z-20"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    جائزة
                  </motion.div>
                )}
                {isOffer && !outOfStock && (
                  <motion.div
                    className="absolute top-2 right-2 bg-[#F5A623] text-white text-xs px-3 py-1 rounded-full font-bold z-20"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    عرض
                  </motion.div>
                )}
                <div
                  className="aspect-square flex items-center justify-center p-6 relative overflow-hidden"
                  style={{ backgroundColor: product.color }}
                >
                  {(product.image_path || product.image_path2) ? (
                    <ProductImage
                      image={product.image_path}
                      image2={product.image_path2}
                      alt={product.name_ar}
                      className="w-full h-full"
                    />
                  ) : (
                    <motion.h3
                      className="font-heading font-bold text-xl sm:text-2xl text-center text-[#111814] relative z-10"
                      animate={{ scale: [1, 1.02, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {product.name_ar}
                    </motion.h3>
                  )}
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm text-[#5C6E64]">{product.name_ar}</p>
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-bold text-lg ${
                        isPrize ? "text-[#E85D45]" : "text-[#2D4A3E]"
                      }`}
                      dir="ltr"
                    >
                      {Number(product.price).toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
                    </span>
                    <span
                      className={`px-5 py-2 rounded-full text-sm font-medium text-white ${
                        isPrize
                          ? "bg-[#2D4A3E] hover:bg-[#233A30]"
                          : "bg-[#E85D45] hover:bg-[#d54d35]"
                      }`}
                    >
                      اشتري
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
    </div>
  );
};

const Home = () => {
  const { user, logout } = useAuth();
  useCart(); // mounts cart context — values now consumed by FloatingActions
  const navigate = useNavigate();

  // Role-based guard: drivers / mujahezeen / supervisors must NEVER land on the
  // customer storefront — they have their own dedicated work portals. Admins
  // are allowed to peek (they sometimes review the storefront before publishing
  // changes), and unauthenticated visitors can browse the catalog as before.
  useEffect(() => {
    if (!user) return;
    if (user.role === "driver")  { navigate("/sawakeen-jumlaty", { replace: true }); return; }
    if (user.role === "mujahez") { navigate("/mujahez-jumlaty",  { replace: true }); return; }
    if (user.role === "rep")     { navigate("/mushrif-jumlaty",  { replace: true }); return; }
    if (user.role === "mandoob") { navigate("/mandoob",  { replace: true }); return; }
  }, [user, navigate]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [csOpen, setCsOpen] = useState(false);
  const [orderStats, setOrderStats] = useState({ today: 0, previous: 0, total: 0, pending: 0 });
  const [rewardsBalance, setRewardsBalance] = useState(0);
  // Per-piece reward rate is now tier-dependent (150 for A_plus, 50 for B_plus)
  // and comes from /api/rewards/me. Default to 150 until the call resolves.
  const [rewardPerItem, setRewardPerItem] = useState(150);
  const [customerDebt, setCustomerDebt] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showAllMarkets, setShowAllMarkets] = useState(false);
  const [bestSellersAuto, setBestSellersAuto] = useState([]);
  const [sectionSettings, setSectionSettings] = useState({
    orderwin: { visible: true, title_ar: "اطلب واربح", banner_aspect: "wide" },
    offers: { visible: true, title_ar: "العروضات", banner_aspect: "wide" },
    bestsellers: { visible: true, title_ar: "الأكثر مبيعاً", banner_aspect: "wide" },
  });
  const [searchQuery, setSearchQuery] = useState("");
  // Voice search (Whisper STT)
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const voiceRecorderRef = useRef(null);
  const voiceChunksRef = useRef([]);

  const startVoiceSearch = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          channelCount: 1,
          sampleRate: 16000,
        },
      });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 24000 });
      voiceChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) voiceChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(voiceChunksRef.current, { type: mime });
        if (blob.size < 400) { setVoiceBusy(false); return; }
        setVoiceBusy(true);
        try {
          const fd = new FormData();
          const ext = mime.includes("webm") ? "webm" : "mp4";
          fd.append("audio", blob, `voice.${ext}`);
          const { data } = await axios.post(`${API}/ai/stt`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 15000,
          });
          if (data.text) {
            setSearchQuery(data.text);
            notify(`بحث صوتي: ${data.text}`);
          } else {
            notify("لم أسمع شيئاً واضحاً");
          }
        } catch {
          notify("تعذّر التعرّف على الصوت");
        } finally {
          setVoiceBusy(false);
        }
      };
      // Use a small timeslice so chunks flow continuously and stop() is responsive
      mr.start(250);
      voiceRecorderRef.current = mr;
      setVoiceRecording(true);
    } catch {
      notify("لا يمكن الوصول للميكروفون");
    }
  };

  const stopVoiceSearch = () => {
    const mr = voiceRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
      setVoiceRecording(false);
    }
  };
  const [banners, setBanners] = useState([]);
  const orderStatusesRef = useRef(new Map()); // order_id -> status (for change detection)

  // Poll customer's orders for status changes to play voice notifications
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/my`);
        if (cancelled) return;
        const prev = orderStatusesRef.current;
        let didNotify = false;
        for (const o of data) {
          const prevStatus = prev.get(o.id);
          if (prevStatus && prevStatus !== o.status && customerStatusVoice[o.status]) {
            if (!didNotify) {
              // Only play one voice per poll cycle to avoid overlap
              notify(customerStatusVoice[o.status]);
              didNotify = true;
            }
          }
          prev.set(o.id, o.status);
        }
        // Refresh customer debt too (in case admin approved payment)
        try {
          const debtRes = await axios.get(`${API}/me/debts`);
          if (!cancelled) setCustomerDebt(debtRes.data?.total_debt || 0);
        } catch {/* ignore */}
      } catch {/* ignore */}
    };
    // Initial seed of statuses without triggering voice (use first poll then enable)
    const init = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/my`);
        for (const o of data) orderStatusesRef.current.set(o.id, o.status);
      } catch {/* ignore */}
    };
    init();
    const interval = setInterval(poll, 12000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        const [cats, prods, stats, leader, bnr, rewards, debts, settings, bestSellersRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products`),
          axios.get(`${API}/orders/stats`).catch(() => ({ data: { today: 0, previous: 0, total: 0, pending: 0 } })),
          axios.get(`${API}/leaderboard`, { params: { limit: 100 } }).catch(() => ({ data: [] })),
          axios.get(`${API}/banners`).catch(() => ({ data: [] })),
          axios.get(`${API}/rewards/me`).catch(() => ({ data: { balance: 0 } })),
          axios.get(`${API}/me/debts`).catch(() => ({ data: { total_debt: 0 } })),
          axios.get(`${API}/settings/public`).catch(() => ({ data: null })),
          axios.get(`${API}/products/best-sellers`).catch(() => ({ data: [] })),
        ]);
        setCategories(cats.data);
        setProducts(prods.data);
        setOrderStats(stats.data);
        setLeaderboard(leader.data);
        setBanners(bnr.data);
        setRewardsBalance(rewards.data?.balance || 0);
        if (rewards.data?.reward_per_item) setRewardPerItem(rewards.data.reward_per_item);
        setCustomerDebt(debts.data?.total_debt || 0);
        setBestSellersAuto(bestSellersRes.data || []);
        if (settings.data?.sections) {
          setSectionSettings((prev) => ({ ...prev, ...settings.data.sections }));
        }
      } catch (e) {
        console.error("Error fetching:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const offersSlides = [
    { title: "خصومات اليوم", subtitle: "Today's Deals", color: "#0EA5E9" },
    { title: "عروض حصرية", subtitle: "Exclusive Offers", color: "#F5A623" },
    { title: "وفّر أكثر", subtitle: "Save More", color: "#2D4A3E" },
  ];

  const bestSellersSlides = [
    { title: "عروض طازجة", subtitle: "Fresh Arrivals", color: "#2D4A3E" },
    { title: "صفقات الجملة", subtitle: "Wholesale Deals", color: "#E85D45" },
    { title: "عروض الأسبوع", subtitle: "Weekly Specials", color: "#4A6F5E" },
  ];

  const orderWinSlides = [
    { title: "اطلب واربح", subtitle: "Order & Win", color: "#2D4A3E" },
    { title: "خصومات يومية", subtitle: "Daily Discounts", color: "#E85D45" },
    { title: "نقاط المكافآت", subtitle: "Reward Points", color: "#4A6F5E" },
  ];

  const offersBanners = banners.filter((b) => b.section === "offers");
  const bestSellersBanners = banners.filter((b) => b.section === "bestsellers");
  const orderWinBanners = banners.filter((b) => b.section === "orderwin");

  // Search functionality — uses Arabic-aware `smartMatch` so the shopper
  // can type "احمد"/"أحمد"/"اَحمد", "مدرسه"/"مدرسة", "موسي"/"موسى",
  // Arabic-Indic digits ("٥٠٠") or spoken numbers ("نص كيلو"/"الف") and
  // still hit the matching product.
  //
  // Performance (Feb 28, 2026):
  //   • `useDeferredValue` lets React keep the input snappy while the
  //     filter runs in the background — no more lag while typing.
  //   • `useMemo` caches the results so we don't refilter on unrelated
  //     re-renders (banners loading, header opening, etc.).
  //   • The searchable string per product is precomputed once when the
  //     product list changes — cuts the per-keystroke work in half.
  const normalizedQuery = searchQuery.trim();
  const deferredQuery = useDeferredValue(normalizedQuery);
  const productHaystacks = useMemo(
    () => products.map((p) => `${p.name_ar || ""} ${p.name || ""} ${p.category || ""} ${p.barcode || ""}`),
    [products]
  );
  const searchResults = useMemo(() => {
    if (!deferredQuery) return [];
    return inStockFirst(products.filter((_, i) => smartMatch(productHaystacks[i], deferredQuery)));
  }, [products, productHaystacks, deferredQuery]);

  // "Show more" state per-section. When false (default) we show 6 items;
  // when true we show ALL matching items in that bucket.
  const [showAllOffers, setShowAllOffers] = useState(false);
  const [showAllBestSellers, setShowAllBestSellers] = useState(false);

  // Filter products into sections based on admin-set flags.
  // ── Featured Offer ordering ──
  // Admins can pin up to 6 offer products to slots 1..6. Those pinned
  // products always come FIRST in the offers section, ordered by their
  // slot number. All other offer products (no slot) come after, keeping
  // their original order. The customer never sees the slot numbers.
  const offersAll = products.filter((p) => p.is_offer && isInStock(p)).slice().sort((a, b) => {
    const sa = Number(a.featured_offer_slot || 0);
    const sb = Number(b.featured_offer_slot || 0);
    if (sa && sb) return sa - sb;
    if (sa) return -1;
    if (sb) return 1;
    return 0;
  });
  // Featured Offer slots are 1..12 (Feb 13, 2026 — expanded from 6). The
  // customer sees up to 12 offer products by default, then a "show more"
  // button reveals the rest.
  const offersProducts = showAllOffers ? offersAll : offersAll.slice(0, 12);
  // Best sellers: auto-computed from order quantities (server-side). Fallback to is_best_seller flag.
  const bestSellersBase = (bestSellersAuto.length > 0
    ? bestSellersAuto
    : products.filter((p) => p.is_best_seller)).filter(isInStock);
  const bestSellers = showAllBestSellers ? bestSellersBase : bestSellersBase.slice(0, 6);

  return (
    <div className="min-h-screen bg-transparent relative">

      {/* Header */}
      <header
        data-testid="header"
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/40 border-b border-[#BAE6FD]/60"
      >
        <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl w-full px-4 sm:px-6 py-3 space-y-3">
          {/* Search Bar */}
          <div className="relative flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
                placeholder={voiceRecording ? "🎙️ يتم التسجيل... اضغط الميكروفون للتوقف" : voiceBusy ? "⏳ جاري التعرّف..." : "ابحث عن المنتج الذي ترغب به"}
                className="w-full pr-10 pl-10 py-2.5 bg-[#E0F2FE] border border-[#BAE6FD] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] placeholder-[#5C6E64]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  data-testid="search-clear"
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-1 hover:bg-[#BAE6FD] rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-[#5C6E64]" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => (voiceRecording ? stopVoiceSearch() : startVoiceSearch())}
              disabled={voiceBusy}
              data-testid="search-mic"
              title="بحث صوتي — اضغط للتحدّث"
              className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-white shadow transition-colors ${
                voiceRecording
                  ? "bg-[#E85D45] animate-pulse"
                  : voiceBusy
                  ? "bg-[#5C6E64]"
                  : "bg-[#0EA5E9] hover:bg-[#0284c7]"
              }`}
            >
              {voiceRecording ? <MicStop className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          </div>

          {/* Logo Row */}
          <div className="flex justify-between items-center">
            <JumlatyLogo size="md" showTagline={false} />
            <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                data-testid="user-menu-btn"
                className="flex items-center gap-2 px-3 py-2 bg-[#E0F2FE] hover:bg-[#BAE6FD] rounded-full transition-colors"
              >
                <User className="w-4 h-4 text-[#2D4A3E]" />
                <span className="text-sm font-medium text-[#2D4A3E] hidden sm:inline">
                  {user?.full_name?.split(" ")[0]}
                </span>
              </button>
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute left-0 top-full mt-2 w-48 bg-white border border-[#BAE6FD] rounded-2xl shadow-lg overflow-hidden z-50"
                >
                  <Link
                    to="/my-orders"
                    onClick={() => setShowUserMenu(false)}
                    data-testid="menu-my-orders"
                    className="flex items-center gap-2 px-4 py-3 hover:bg-[#E0F2FE] transition-colors"
                  >
                    <Package className="w-4 h-4 text-[#2D4A3E]" />
                    <span className="text-sm">طلباتي</span>
                  </Link>
                  <Link
                    to="/rewards"
                    onClick={() => setShowUserMenu(false)}
                    data-testid="menu-rewards"
                    className="flex items-center gap-2 px-4 py-3 hover:bg-[#E0F2FE] transition-colors border-t border-[#BAE6FD]"
                  >
                    <Gift className="w-4 h-4 text-[#F5A623]" />
                    <span className="text-sm">أرباحي</span>
                  </Link>
                  <Link
                    to="/complaints"
                    onClick={() => setShowUserMenu(false)}
                    data-testid="menu-complaints"
                    className="flex items-center gap-2 px-4 py-3 hover:bg-[#E0F2FE] transition-colors border-t border-[#BAE6FD]"
                  >
                    <AlertCircle className="w-4 h-4 text-[#E85D45]" />
                    <span className="text-sm">الشكاوي والملاحظات</span>
                  </Link>
                  <Link
                    to="/my-locations"
                    onClick={() => setShowUserMenu(false)}
                    data-testid="menu-my-locations"
                    className="flex items-center gap-2 px-4 py-3 hover:bg-[#E0F2FE] transition-colors border-t border-[#BAE6FD]"
                  >
                    <MapPin className="w-4 h-4 text-[#15803D]" />
                    <span className="text-sm">مواقعي</span>
                  </Link>
                  <Link
                    to="/my-account"
                    onClick={() => setShowUserMenu(false)}
                    data-testid="menu-my-account"
                    className="flex items-center gap-2 px-4 py-3 hover:bg-[#E0F2FE] transition-colors border-t border-[#BAE6FD]"
                  >
                    <User className="w-4 h-4 text-[#2D4A3E]" />
                    <span className="text-sm font-bold">إدارة حسابي</span>
                  </Link>
                  {user?.role === "admin" && (
                    <Link
                      to="/admin"
                      onClick={() => setShowUserMenu(false)}
                      data-testid="menu-admin"
                      className="flex items-center gap-2 px-4 py-3 hover:bg-[#E0F2FE] transition-colors border-t border-[#BAE6FD]"
                    >
                      <User className="w-4 h-4 text-[#E85D45]" />
                      <span className="text-sm">لوحة الإدارة</span>
                    </Link>
                  )}
                  <button
                    onClick={async () => {
                      const ok = await confirmDialog("هل تريد تسجيل الخروج؟", {
                        force: true,
                        tone: "danger",
                        title: "تسجيل الخروج",
                        confirmLabel: "نعم، تسجيل الخروج",
                        cancelLabel: "لا، تراجع",
                      });
                      if (!ok) return;
                      logout();
                      setShowUserMenu(false);
                      navigate("/login");
                    }}
                    data-testid="menu-logout"
                    className="flex items-center gap-2 px-4 py-3 hover:bg-[#E0F2FE] transition-colors border-t border-[#BAE6FD] w-full text-right"
                  >
                    <LogOut className="w-4 h-4 text-[#E85D45]" />
                    <span className="text-sm">خروج</span>
                  </button>
                </motion.div>
              )}
            </div>
            <button
              onClick={() => setCsOpen(true)}
              data-testid="header-cs-btn"
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-br from-[#6B8E45] to-[#4A6F36] hover:from-[#7a9f50] hover:to-[#5a8042] text-white rounded-full transition-all shadow-md"
              title="خدمة العملاء"
            >
              <motion.span
                animate={{ rotate: [0, -8, 8, -8, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                className="flex"
              >
                <Headphones className="w-4 h-4" />
              </motion.span>
              <span className="text-xs font-bold hidden sm:inline whitespace-nowrap">
                خدمة العملاء
              </span>
            </button>
            {user?.role === "admin" && (
              <button
                onClick={() => navigate("/admin")}
                data-testid="header-admin-switch"
                className="flex items-center gap-2 px-3 py-2 bg-[#2D4A3E] hover:bg-[#1f3529] text-white rounded-full transition-all shadow-md"
                title="العودة للإدارة"
              >
                <UserCog className="w-4 h-4" />
                <span className="text-xs font-bold hidden sm:inline whitespace-nowrap">
                  العودة للإدارة
                </span>
              </button>
            )}
          </div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl w-full px-4 sm:px-6 py-6 space-y-10 relative z-10">
        {/* Search Results */}
        {normalizedQuery && (
          <section data-testid="search-results">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111814] mb-4 tracking-tight flex items-center gap-2">
              <Search className="w-6 h-6 text-[#0EA5E9]" />
              نتائج البحث ({searchResults.length})
            </h2>
            {searchResults.length === 0 ? (
              <div className="text-center py-12 bg-[#E0F2FE] rounded-2xl">
                <p className="text-[#5C6E64]">لا توجد منتجات مطابقة لـ "{searchQuery}"</p>
              </div>
            ) : (
              <ProductGrid
                products={searchResults}
                loading={false}
                onSelect={setSelectedProduct}
                variant="default"
                testIdPrefix="search-product"
              />
            )}
          </section>
        )}

        {/* Customer Order Stats Card */}
        {!normalizedQuery && (
        <>
        {/* Rewards Balance Card — compact (was p-5 / text-3xl → too tall on
            mobile per Feb 17 user feedback). Now p-3 + text-2xl so the
            balance stays legible while the 4 stat boxes rise higher on
            the fold. */}
        <Link
          to="/rewards"
          data-testid="rewards-card"
          className="block relative bg-gradient-to-l from-[#F5A623] via-[#E85D45] to-[#E85D45] text-white rounded-2xl p-3 hover:shadow-xl transition-all overflow-hidden group"
        >
          <Sparkles className="absolute -top-2 -right-2 w-16 h-16 opacity-15 group-hover:rotate-12 transition-transform" />
          <Gift className="absolute -bottom-3 -left-3 w-20 h-20 opacity-10" />
          <div className="relative z-10 flex justify-between items-center">
            <div>
              <p className="text-[11px] opacity-90 mb-0.5 flex items-center gap-1">
                <Gift className="w-3 h-3" />
                رصيد أرباحك
              </p>
              <p className="font-bold text-2xl leading-tight" data-testid="home-rewards-balance">
                {Number(rewardsBalance || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </p>
              <p className="text-[10px] opacity-80">دينار عراقي · <span dir="ltr">{rewardPerItem}</span> د.ع لكل قطعة</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-full px-3 py-1.5 text-[11px] font-bold whitespace-nowrap">
              عرض ومسحب →
            </div>
          </div>
        </Link>

        {/* Stat tiles — all four boxes now identical compact size (p-3 +
            text-xl + text-[11px] label) so the debt card no longer bulges
            taller than the others. Feb 17, 2026 UI polish. */}
        <section data-testid="my-orders-stats" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Link
            to="/my-orders"
            data-testid="stat-today"
            className="bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] text-white rounded-2xl p-3 hover:shadow-lg transition-shadow"
          >
            <Calendar className="w-4 h-4 mb-1 opacity-90" />
            <p className="text-[11px] opacity-90">طلباتي اليوم</p>
            <p className="font-bold text-xl leading-tight">{orderStats.today}</p>
          </Link>
          <Link
            to="/my-orders"
            data-testid="stat-previous"
            className="bg-gradient-to-br from-[#2D4A3E] to-[#1f3329] text-white rounded-2xl p-3 hover:shadow-lg transition-shadow"
          >
            <History className="w-4 h-4 mb-1 opacity-90" />
            <p className="text-[11px] opacity-90">الطلبات السابقة</p>
            <p className="font-bold text-xl leading-tight">{orderStats.previous}</p>
          </Link>
          <Link
            to="/my-orders"
            data-testid="stat-pending"
            className="bg-gradient-to-br from-[#F5A623] to-[#d68d10] text-white rounded-2xl p-3 hover:shadow-lg transition-shadow"
          >
            <Package className="w-4 h-4 mb-1 opacity-90" />
            <p className="text-[11px] opacity-90">قيد المعالجة</p>
            <p className="font-bold text-xl leading-tight">{orderStats.pending}</p>
          </Link>
          <Link
            to="/debts"
            data-testid="stat-debt"
            className={`rounded-2xl p-3 hover:shadow-lg transition-shadow text-white ${
              customerDebt > 0
                ? "bg-gradient-to-br from-[#E85D45] to-[#c14a36] ring-2 ring-[#E85D45]/40 animate-pulse"
                : "bg-gradient-to-br from-[#5C6E64] to-[#3d4a44]"
            }`}
          >
            <FileText className="w-4 h-4 mb-1 opacity-90" />
            <p className="text-[11px] opacity-90">المبلغ المتبقي</p>
            <p className="font-bold text-xl leading-tight" data-testid="home-debt-amount">
              {Number(customerDebt || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
            </p>
            <p className="text-[9px] opacity-80 leading-tight">د.ع</p>
          </Link>
        </section>
        </>
        )}

        {!normalizedQuery && <>
        {/* 1. Offers Section — banner FIRST, then heading, then products */}
        <section data-testid="offers-section">
          <div className="mb-4">
            <ImageCarousel
              banners={offersBanners}
              defaultSlides={offersSlides}
              testId="offers-carousel"
              aspect={aspectInfo(sectionSettings.offers?.banner_aspect).aspectCss}
            />
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#111814] mb-6 tracking-tight flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-[#F5A623]" />
            {sectionSettings.offers?.title_ar || "العروضات"}
          </h2>
          <ProductGrid
            products={offersProducts}
            loading={loading}
            onSelect={setSelectedProduct}
            variant="offer"
            testIdPrefix="offer-product"
          />
          {offersAll.length > 12 && (
            <div className="text-center mt-6">
              <button
                data-testid="offers-show-more"
                onClick={() => setShowAllOffers((v) => !v)}
                className="px-8 py-3 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#2D4A3E] font-medium rounded-full transition-colors"
              >
                {showAllOffers ? `عرض أقل (${offersAll.length} منتج)` : `عرض المزيد (${offersAll.length})`}
              </button>
            </div>
          )}
        </section>

        {/* 2. Categories Section (NO Show More) */}
        <section data-testid="categories-section">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#111814] mb-6 tracking-tight">
            الأقسام
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square bg-[#E0F2FE] rounded-2xl animate-pulse"
                  />
                ))
              : categories.map((category, index) => {
                  const IconComponent = iconMap[category.icon];
                  const isPlaceholder = category.icon === "placeholder";
                  return (
                    <motion.button
                      key={category.id}
                      data-testid={`category-${category.icon}-${index}`}
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={!isPlaceholder ? { y: -4, scale: 1.05 } : {}}
                      whileTap={!isPlaceholder ? { scale: 0.95 } : {}}
                      onClick={() => !isPlaceholder && navigate(`/category/${category.id}`)}
                      disabled={isPlaceholder}
                      className={`aspect-square rounded-2xl flex flex-col items-center justify-center gap-2 transition-all relative overflow-hidden bg-[#E0F2FE] border border-[#BAE6FD] ${
                        isPlaceholder ? "opacity-50 cursor-not-allowed" : "hover:shadow-lg"
                      }`}
                    >
                      {IconComponent && (
                        <motion.div
                          animate={!isPlaceholder ? {
                            scale: [1, 1.1, 1],
                            rotate: [0, 5, -5, 0],
                          } : {}}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            repeatType: "reverse",
                          }}
                          className="relative z-10"
                        >
                          <IconComponent
                            className="w-8 h-8 sm:w-10 sm:h-10"
                            style={{ color: category.color }}
                          />
                        </motion.div>
                      )}
                      <span className="text-xs sm:text-sm font-medium text-[#111814] relative z-10">
                        {category.name_ar || "قريباً"}
                      </span>
                    </motion.button>
                  );
                })}
          </div>
        </section>

        {/* 3. Top Markets — Olympic Podium (always rendered so the
            ContestStrip + empty-state remain visible right after the admin
            resets the contest; previously the whole section disappeared
            when `leaderboard.length === 0`). */}
        <section data-testid="top-markets-section">
          <div data-testid="leaderboard" className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-[#F5A623]" />
                  <h3 className="font-heading font-bold text-xl sm:text-2xl text-[#111814]">
                    أكثر الماركتات طلباً
                  </h3>
                </div>
                {leaderboard.length > 3 && (
                  <button
                    onClick={() => setShowAllMarkets(true)}
                    data-testid="leaderboard-show-all"
                    className="text-xs font-bold text-[#3B82F6] hover:text-[#1E3A5F] flex items-center gap-1 px-3 py-1.5 bg-[#E0F2FE] rounded-full"
                  >
                    عرض الكل (<span dir="ltr">{leaderboard.length}</span>)
                  </button>
                )}
              </div>

              {/* Monthly contest countdown — sits between the heading and the
                  podium so it's clearly tied to "Top Markets". Triggers the
                  big celebration modal when the timer hits zero. */}
              <div className="mb-4">
                <ContestStrip />
              </div>

              {/* Olympic Podium Grid — only render when at least one
                  market has orders this cycle. Empty state below replaces it. */}
              {leaderboard.length > 0 && (
              <button
                onClick={() => leaderboard.length > 0 && setShowAllMarkets(true)}
                data-testid="podium-clickable"
                className="w-full bg-gradient-to-br from-[#FFFBEB] via-white to-[#FEF3C7] border-2 border-[#F5A623]/30 rounded-3xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow text-right"
              >
                {/* Top — Gold (#1) */}
                {leaderboard[0] && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    data-testid="leaderboard-rank-1"
                    className="flex flex-col items-center mb-4"
                  >
                    <div className="relative">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F5A623] text-white text-[10px] font-black px-3 py-0.5 rounded-full shadow-md flex items-center gap-1 z-10">
                        <Crown className="w-3 h-3" />
                        رقم <span dir="ltr">1</span>
                      </div>
                      <div className="bg-gradient-to-br from-[#FFD700] to-[#FFA500] shadow-[0_12px_32px_-8px_rgba(255,165,0,0.7)] rounded-2xl p-4 text-white w-44 sm:w-56 text-center scale-105 border-4 border-white">
                        <Crown className="w-10 h-10 mx-auto mb-1 drop-shadow" />
                        <p className="text-[10px] opacity-90 mb-0.5">ذهبي</p>
                        <p className="font-bold text-base sm:text-lg truncate" data-testid="market-name-1">
                          {leaderboard[0].market_name}
                        </p>
                        <div className="mt-2 pt-2 border-t border-white/40 flex items-baseline justify-center gap-1">
                          <span className="font-black text-2xl sm:text-3xl" dir="ltr">
                            {leaderboard[0].total_quantity}
                          </span>
                          <span className="text-[10px] opacity-90">قطعة</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Bottom row — Silver (right) + Bronze (left) — Olympic style */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="order-1">
                    {leaderboard[1] ? (
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.25 }}
                        data-testid="leaderboard-rank-2"
                        className="bg-gradient-to-br from-[#D1D5DB] to-[#9CA3AF] shadow-[0_8px_20px_-6px_rgba(156,163,175,0.6)] rounded-2xl p-3 text-white text-center border-2 border-white relative"
                      >
                        <div className="absolute -top-2 right-2 bg-[#9CA3AF] text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow">
                          <span dir="ltr">2</span>
                        </div>
                        <Medal className="w-7 h-7 mx-auto mb-1 drop-shadow" />
                        <p className="text-[10px] opacity-90 mb-0.5">فضي</p>
                        <p className="font-bold text-sm truncate" data-testid="market-name-2">
                          {leaderboard[1].market_name}
                        </p>
                        <div className="mt-1.5 pt-1.5 border-t border-white/40">
                          <span className="font-black text-lg sm:text-xl" dir="ltr">
                            {leaderboard[1].total_quantity}
                          </span>
                          <span className="text-[10px] opacity-90 mr-1">قطعة</span>
                        </div>
                      </motion.div>
                    ) : (
                      <div className="rounded-2xl p-3 bg-white/40 border-2 border-dashed border-[#9CA3AF]/40 text-center text-xs text-[#5C6E64] flex items-center justify-center min-h-[110px]">
                        — فاضي —
                      </div>
                    )}
                  </div>
                  <div className="order-2">
                    {leaderboard[2] ? (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                        data-testid="leaderboard-rank-3"
                        className="bg-gradient-to-br from-[#CD7F32] to-[#A0522D] shadow-[0_8px_20px_-6px_rgba(160,82,45,0.5)] rounded-2xl p-3 text-white text-center border-2 border-white relative"
                      >
                        <div className="absolute -top-2 right-2 bg-[#A0522D] text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow">
                          <span dir="ltr">3</span>
                        </div>
                        <Medal className="w-7 h-7 mx-auto mb-1 drop-shadow" />
                        <p className="text-[10px] opacity-90 mb-0.5">برونزي</p>
                        <p className="font-bold text-sm truncate" data-testid="market-name-3">
                          {leaderboard[2].market_name}
                        </p>
                        <div className="mt-1.5 pt-1.5 border-t border-white/40">
                          <span className="font-black text-lg sm:text-xl" dir="ltr">
                            {leaderboard[2].total_quantity}
                          </span>
                          <span className="text-[10px] opacity-90 mr-1">قطعة</span>
                        </div>
                      </motion.div>
                    ) : (
                      <div className="rounded-2xl p-3 bg-white/40 border-2 border-dashed border-[#A0522D]/40 text-center text-xs text-[#5C6E64] flex items-center justify-center min-h-[110px]">
                        — فاضي —
                      </div>
                    )}
                  </div>
                </div>

                {leaderboard.length > 3 && (
                  <p className="text-center text-[11px] text-[#5C6E64] mt-3">
                    اضغط لعرض كل الـ <span dir="ltr" className="font-bold">{leaderboard.length}</span> ماركت
                  </p>
                )}
              </button>
              )}

              {/* Empty state — shown right after admin resets the contest
                  (no orders yet in the new cycle). Encourages the customer
                  to be the first to start ordering. */}
              {leaderboard.length === 0 && (
                <div
                  data-testid="leaderboard-empty"
                  className="bg-gradient-to-br from-[#FFFBEB] via-white to-[#FEF3C7] border-2 border-dashed border-[#F5A623]/40 rounded-3xl p-6 text-center"
                >
                  <Trophy className="w-12 h-12 mx-auto text-[#F5A623] opacity-60" />
                  <h4 className="font-extrabold text-base text-[#a06e10] mt-2">السباق بدأ من جديد!</h4>
                  <p className="text-sm text-[#7c5510] mt-1">
                    لا يزال لم يطلب أحد بعد. كُن أول ماركت يتصدّر هذا الشهر — 🥇
                  </p>
                </div>
              )}
            </div>
        </section>
        {/* 4. Best Sellers Section — banner FIRST, then heading, then products */}
        <section data-testid="best-sellers-section">
          <div className="mb-4">
            <ImageCarousel
              banners={bestSellersBanners}
              defaultSlides={bestSellersSlides}
              testId="bestsellers-carousel"
              aspect={aspectInfo(sectionSettings.bestsellers?.banner_aspect).aspectCss}
            />
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#111814] mb-6 tracking-tight">
            {sectionSettings.bestsellers?.title_ar || "الأكثر مبيعاً"}
          </h2>
          <ProductGrid
            products={bestSellers}
            loading={loading}
            onSelect={setSelectedProduct}
            variant="default"
            testIdPrefix="product"
          />
          {bestSellersBase.length > 6 && (
            <div className="text-center mt-6">
              <button
                data-testid="bestsellers-show-more"
                onClick={() => setShowAllBestSellers((v) => !v)}
                className="px-8 py-3 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#2D4A3E] font-medium rounded-full transition-colors"
              >
                {showAllBestSellers ? `عرض أقل (${bestSellersBase.length} منتج)` : `عرض المزيد (${bestSellersBase.length})`}
              </button>
            </div>
          )}
        </section>


        </>}
      </main>

      <footer className="mt-16 py-8 border-t border-[#BAE6FD] relative z-10">
        <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl px-4 text-center">
          <p className="text-[#5C6E64] text-sm">
            © 2024 جملتي. جميع الحقوق محفوظة
          </p>
        </div>
      </footer>

      <ProductModal
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
      <CustomerServiceModal isOpen={csOpen} onClose={() => setCsOpen(false)} />

      {/* Top-100 markets modal */}
      {showAllMarkets && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          dir="rtl"
          data-testid="all-markets-modal"
          onClick={() => setShowAllMarkets(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-l from-[#FFD700] to-[#F5A623] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                <h3 className="font-bold text-lg">ترتيب الماركتات</h3>
                <span className="bg-white/25 px-2 py-0.5 rounded-full text-[10px] font-bold" dir="ltr">
                  {leaderboard.length}
                </span>
              </div>
              <button
                onClick={() => setShowAllMarkets(false)}
                data-testid="all-markets-close"
                className="p-1.5 bg-white/20 hover:bg-white/30 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {leaderboard.map((item, i) => {
                const rank = i + 1;
                const isGold = rank === 1, isSilver = rank === 2, isBronze = rank === 3;
                const tier = isGold ? "bg-gradient-to-l from-[#FFD700] to-[#FFA500]"
                  : isSilver ? "bg-gradient-to-l from-[#D1D5DB] to-[#9CA3AF]"
                  : isBronze ? "bg-gradient-to-l from-[#CD7F32] to-[#A0522D]"
                  : "bg-[#F3F6F4]";
                const txt = (isGold || isSilver || isBronze) ? "text-white" : "text-[#111814]";
                return (
                  <motion.div
                    key={item.user_id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.015 }}
                    data-testid={`all-markets-row-${rank}`}
                    className={`flex items-center justify-between p-3 rounded-xl ${tier} ${txt}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-black shrink-0 ${
                        (isGold || isSilver || isBronze) ? "bg-white/25" : "bg-[#E0F2FE] text-[#1E3A5F]"
                      }`} dir="ltr">
                        {rank}
                      </div>
                      <p className="font-bold truncate">{item.market_name}</p>
                    </div>
                    <div className="text-left shrink-0">
                      <p className="font-black text-lg" dir="ltr">{item.total_quantity}</p>
                      <p className="text-[10px] opacity-90">قطعة</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <BackToAdminButton />
    </div>
  );
};

export default Home;
